import { useRender } from '@base-ui/react';
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

const LevelContext = createContext<number>(0);

export function useContainerLevel(): number {
    return useContext(LevelContext);
}

export type ContainerProps = useRender.ComponentProps<'div'> & {
    level?: number;
    minLevel?: number;
    nestLevel?: number;
};

export function Container({ level, minLevel, nestLevel, ...props }: ContainerProps): ReactElement {
    return (
        <ContainerContext level={level} minLevel={minLevel} nestLevel={nestLevel}>
            <ContainerSurface {...props} />
        </ContainerContext>
    );
}

type ContainerSurfaceProps = useRender.ComponentProps<'div'>;

function ContainerSurface({ render, ...props }: ContainerSurfaceProps): ReactElement {
    const level = useContainerLevel();
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            'data-container': `${level}`,
            'data-level': `${level}`,
            ...props,
        },
    });
}

export type ContainerContextProps = {
    children?: ReactNode;
    level?: number;
    minLevel?: number;
    nestLevel?: number;
};

export function ContainerContext({
    children,
    level,
    minLevel = 0,
    nestLevel = 1,
}: ContainerContextProps): ReactElement {
    const inheritedLevel = useContainerLevel();
    const computedLevel = level ?? Math.max(minLevel, inheritedLevel + Math.max(0, nestLevel));
    return <LevelContext.Provider value={computedLevel}>{children}</LevelContext.Provider>;
}
